import React, { useState, useEffect } from "react";
import { CircularProgress, Modal, Switch } from "@mui/material";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import { clientRoutes } from "../../../utils/routes";
import { BrandApi } from "../../../utils/api/Brand";
import { AgencyApi } from "../../../utils/api/Agency";
import { AgentApi } from "../../../utils/api/Agent";
import { CommonEnums } from "../../../enums/common.enums";

import CustomPageLoader from "../../../components/loaders/CustomPageLoader";
import ProtectedComponent from "../../../components/ProtectedComponent";

import AgencyProfileLogo from "./AgencyProfileLogo";
import EditThemePreferences from "./EditThemePreferences";
import ThemePreferences from "./ThemePreferences";
import ThemeErrorSummary from "./ThemeErrorSummary";
import BrandProfileLogo from "./BrandProfileLogo";
import ErrorSummary from "./ErrorSummary";
import AgentCreatingAgencySteps from "./AgencyProfileSteps";

import "./BrandGrammar.scss";
import { getCdnImageUrl, getImageUrl } from "../../../utils/helpers/common";

const AgentAgencyBrandGrammar = () => {
  const navigate = useNavigate();
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [contrastWarnings, setContrastWarnings] = useState({});
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const openLogoutModal = () => setIsLogoutModalOpen(true);
  const closeLogoutModal = () => setIsLogoutModalOpen(false);
  const [state, setState] = useState({
    isLoading: false,
    loadingData: true,
    agencyData: null,
    isBrandAgency: false,
    brandName: "",
    brandStatus: "",
    isBrandDataLoading: false,
  });

  const [agencyOriginalColors, setAgencyOriginalColors] = useState({
    base_color_primary: null,
    base_color_secondary: null,
    text_color_primary: null,
    text_color_secondary: null,
  });

  const [brandOriginalColors, setBrandOriginalColors] = useState({
    base_color_primary: null,
    base_color_secondary: null,
    text_color_primary: null,
    text_color_secondary: null,
  });

  const normalizeColor = (color) => {
    if (!color) return null;

    if (
      color?.rgb?.r != null &&
      color?.rgb?.g != null &&
      color?.rgb?.b != null
    ) {
      return color;
    }

    if (!color?.hex) return color;

    const clean = color.hex.replace("#", "");

    const rgb = {
      r: parseInt(clean.substring(0, 2), 16),
      g: parseInt(clean.substring(2, 4), 16),
      b: parseInt(clean.substring(4, 6), 16),
    };

    return {
      hex: color.hex,
      rgb,
    };
  };

  const validateBrandGrammarForm = (values) => {
    const errors = {};
    const hasAgencyLogo = !!values.profileLogo;

    if (!state.isBrandAgency && !hasAgencyLogo) {
      errors.profileLogo = "is required";
    }

    if (state.isBrandAgency && values.useOfficeLogo && !hasAgencyLogo) {
      errors.profileLogo = "is required";
    }

    if (state.isBrandAgency) {
      if (!values.brandLicensing) {
        errors.brandLicensing = "Please confirm the Brand Licensing terms.";
      }

      if (!values.logoUsageRights) {
        errors.logoUsageRights = "Please confirm logo usage rights.";
      }
    }

    return errors;
  };

  const fetchDeclarations = async (agencyId, isBrand) => {
    const declarationsMap = isBrand
      ? [
          { key: "brandLicensing", type: "brand_licensing" },
          { key: "logoUsageRights", type: "logo_usage_rights" },
        ]
      : [];

    try {
      const responses = await Promise.all(
        declarationsMap.map((item) =>
          AgencyApi.getAgencyDeclaration({
            agency_id: agencyId,
            declaration_type: item.type,
          }),
        ),
      );

      responses.forEach((res, index) => {
        if (res?.data?.success) {
          const key = declarationsMap[index].key;
          formik.setFieldValue(key, res.data?.data?.is_accepted || false);
        }
      });
    } catch (error) {
      console.error("Failed to fetch declarations:", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      profileLogo: "",
      profileLogoUploadRecord: null,
      profileLogoFile: null,
      profileLogoPreview: "",
      baseColor: "",
      useOfficeLogo: false,

      brand_logo: "",
      brandLogoUploadRecord: null,
      brandLicensing: false,
      logoUsageRights: false,

      brand_base_color_primary: null,
      brand_base_color_secondary: null,
      brand_text_color_primary: null,
      brand_text_color_secondary: null,
      brand_theme_colors: [],

      agency_base_color_primary: null,
      agency_base_color_secondary: null,
      agency_text_color_primary: null,
      agency_text_color_secondary: null,
      agency_theme_colors: [],
    },
    validate: () => ({}),
    // onSubmit: async (values) => {
    //   const validationErrors = validateBrandGrammarForm(values);

    //   if (Object.keys(validationErrors).length > 0) {
    //     setFormErrors(validationErrors);
    //     return;
    //   }

    //   setFormErrors({});
    //   setState((prev) => ({ ...prev, isLoading: true }));

    //   let agencyData = state.agencyData;
    //   if (!agencyData) {
    //     agencyData = LocalStorageHelpers.getAgencyData();
    //   }

    //   const agencyId = agencyData?._id ?? agencyData?.id;

    //   if (!agencyId) {
    //     toast.error("Agency ID missing. Please login again.");
    //     setState((prev) => ({ ...prev, isLoading: false }));
    //     return;
    //   }

    //   const isBrandAgency = state.isBrandAgency;
    //   const useOfficeLogo = values.useOfficeLogo;
    //   const shouldUseOfficeLogo = !isBrandAgency || useOfficeLogo;

    //   const formData = new FormData();

    //   const activeBasePrimary = isBrandAgency
    //     ? useOfficeLogo
    //       ? values.agency_base_color_primary
    //       : values.brand_base_color_primary
    //     : values.agency_base_color_primary;

    //   const activeBaseSecondary = isBrandAgency
    //     ? useOfficeLogo
    //       ? values.agency_base_color_secondary
    //       : values.brand_base_color_secondary
    //     : values.agency_base_color_secondary;

    //   const activeTextPrimary = isBrandAgency
    //     ? useOfficeLogo
    //       ? values.agency_text_color_primary
    //       : values.brand_text_color_primary
    //     : values.agency_text_color_primary;

    //   const activeTextSecondary = isBrandAgency
    //     ? useOfficeLogo
    //       ? values.agency_text_color_secondary
    //       : values.brand_text_color_secondary
    //     : values.agency_text_color_secondary;

    //   if (activeBasePrimary) {
    //     formData.append(
    //       "base_color_primary",
    //       JSON.stringify(activeBasePrimary),
    //     );
    //   }

    //   if (activeBaseSecondary) {
    //     formData.append(
    //       "base_color_secondary",
    //       JSON.stringify(activeBaseSecondary),
    //     );
    //   }

    //   if (activeTextPrimary) {
    //     formData.append(
    //       "text_color_primary",
    //       JSON.stringify(activeTextPrimary),
    //     );
    //   }

    //   if (activeTextSecondary) {
    //     formData.append(
    //       "text_color_secondary",
    //       JSON.stringify(activeTextSecondary),
    //     );
    //   }

    //   if (isBrandAgency && !useOfficeLogo) {
    //     const brandId = agencyData?.brand_id?._id;

    //     if (!brandId) {
    //       toast.error("Brand ID not found.");
    //       setState((prev) => ({ ...prev, isLoading: false }));
    //       return;
    //     }

    //     const brandPayload = {
    //       base_color_primary: values.brand_base_color_primary,
    //       base_color_secondary: values.brand_base_color_secondary,
    //       text_color_primary: values.brand_text_color_primary,
    //       text_color_secondary: values.brand_text_color_secondary,
    //       theme_colors_response: values.brand_theme_colors,
    //     };

    //     const brandRes = await BrandApi.updateBrandDetails(
    //       brandId,
    //       brandPayload,
    //     );

    //     if (!brandRes.success) {
    //       toast.error(brandRes.error || "Failed to update brand details");
    //       setState((prev) => ({ ...prev, isLoading: false }));
    //       return;
    //     }
    //   }

    //   if (values.baseColor) {
    //     formData.append("agency_base_color", values.baseColor);
    //   }

    //   if (values.profileLogoFile) {
    //     formData.append("profile_image", values.profileLogoFile);
    //   }

    //   formData.append("automatic_timezone", "true");
    //   formData.append(
    //     "logo_preference",
    //     shouldUseOfficeLogo ? "office_logo" : "brand_logo",
    //   );
    //   formData.append("use_office_logo", String(shouldUseOfficeLogo));
    //   formData.append("status", CommonEnums.status.pending_for_admin_approval);
    //   formData.append(
    //     "registration_status",
    //     CommonEnums.agentRegistrationStatus.completed,
    //   );

    //   try {
    //     const res = await AgencyApi.updateAgencyProfile(formData, agencyId);

    //     const principalAgentId =
    //       agencyData?.principal_agent_id?._id || agencyData?.principal_agent_id;

    //     if (principalAgentId) {
    //       await AgentApi.updateAgentProfile(
    //         {
    //           registration_status:
    //             CommonEnums.agentRegistrationStatus.completed,
    //         },
    //         principalAgentId,
    //       );
    //     }

    //     if (res?.data?.success) {
    //       const updatedData = res.data.data;
    //       LocalStorageHelpers.setAgencyData({ data: updatedData });

    //       const declarationsMap = state.isBrandAgency
    //         ? [
    //             { key: "brandLicensing", type: "brand_licensing" },
    //             { key: "logoUsageRights", type: "logo_usage_rights" },
    //           ]
    //         : [];

    //       const selectedDeclarations = declarationsMap.filter(
    //         (item) => values[item.key],
    //       );

    //       for (const item of selectedDeclarations) {
    //         await AgencyApi.saveAgencyDeclaration({
    //           agency_id: agencyId,
    //           declaration_type: item.type,
    //           is_accepted: values[item.key] || false,
    //         });
    //       }

    //       navigate(clientRoutes.adminApprovalWaiting);
    //     } else {
    //       toast.error(res?.data?.message || "Failed to save brand info.");
    //     }
    //   } catch (error) {
    //     console.error("API Error:", error);
    //     toast.error("Something went wrong while saving.");
    //   }

    //   setState((prev) => ({ ...prev, isLoading: false }));
    // },

    onSubmit: async (values) => {
      const validationErrors = validateBrandGrammarForm(values);

      if (Object.keys(validationErrors).length > 0) {
        setFormErrors(validationErrors);
        return;
      }

      setFormErrors({});
      setState((prev) => ({ ...prev, isLoading: true }));

      let agencyData = state.agencyData;
      if (!agencyData) {
        agencyData = LocalStorageHelpers.getAgencyData();
      }

      const agencyId = agencyData?._id ?? agencyData?.id;

      if (!agencyId) {
        toast.error("Agency ID missing. Please login again.");
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const isRejectedByAdmin =
        agencyData?.status === CommonEnums.status.rejected_by_admin;

      const isBrandAgency = state.isBrandAgency;
      const useOfficeLogo = values.useOfficeLogo;
      const shouldUseOfficeLogo = !isBrandAgency || useOfficeLogo;

      const formData = new FormData();

      const activeBasePrimary = isBrandAgency
        ? useOfficeLogo
          ? values.agency_base_color_primary
          : values.brand_base_color_primary
        : values.agency_base_color_primary;
      const activeBaseSecondary = isBrandAgency
        ? useOfficeLogo
          ? values.agency_base_color_secondary
          : values.brand_base_color_secondary
        : values.agency_base_color_secondary;
      const activeTextPrimary = isBrandAgency
        ? useOfficeLogo
          ? values.agency_text_color_primary
          : values.brand_text_color_primary
        : values.agency_text_color_primary;
      const activeTextSecondary = isBrandAgency
        ? useOfficeLogo
          ? values.agency_text_color_secondary
          : values.brand_text_color_secondary
        : values.agency_text_color_secondary;

      if (activeBasePrimary)
        formData.append(
          "base_color_primary",
          JSON.stringify(activeBasePrimary),
        );
      if (activeBaseSecondary)
        formData.append(
          "base_color_secondary",
          JSON.stringify(activeBaseSecondary),
        );
      if (activeTextPrimary)
        formData.append(
          "text_color_primary",
          JSON.stringify(activeTextPrimary),
        );
      if (activeTextSecondary)
        formData.append(
          "text_color_secondary",
          JSON.stringify(activeTextSecondary),
        );
      if (values.baseColor)
        formData.append("agency_base_color", values.baseColor);
      if (values.profileLogoFile)
        formData.append("profile_image", values.profileLogoFile);

      formData.append("automatic_timezone", "true");
      formData.append(
        "logo_preference",
        shouldUseOfficeLogo ? "office_logo" : "brand_logo",
      );
      formData.append("use_office_logo", String(shouldUseOfficeLogo));

      if (isRejectedByAdmin) {
        formData.append(
          "registration_status",
          CommonEnums.agentRegistrationStatus.pending_for_agent_info,
        );
      } else {
        formData.append(
          "status",
          CommonEnums.status.pending_for_admin_approval,
        );
        formData.append(
          "registration_status",
          CommonEnums.agentRegistrationStatus.completed,
        );
      }

      try {
        if (isBrandAgency && !useOfficeLogo) {
          const brandId = agencyData?.brand_id?._id;
          if (brandId) {
            const brandPayload = {
              base_color_primary: values.brand_base_color_primary,
              base_color_secondary: values.brand_base_color_secondary,
              text_color_primary: values.brand_text_color_primary,
              text_color_secondary: values.brand_text_color_secondary,
              theme_colors_response: values.brand_theme_colors,
            };
            await BrandApi.updateBrandDetails(brandId, brandPayload);
          }
        }

        const res = await AgencyApi.updateAgencyProfile(formData, agencyId);

        if (res?.data?.success) {
          const updatedData = res.data.data;
          LocalStorageHelpers.setAgencyData({ data: updatedData });

          const declarationsMap = state.isBrandAgency
            ? [
                { key: "brandLicensing", type: "brand_licensing" },
                { key: "logoUsageRights", type: "logo_usage_rights" },
              ]
            : [];
          const selectedDeclarations = declarationsMap.filter(
            (item) => values[item.key],
          );
          for (const item of selectedDeclarations) {
            await AgencyApi.saveAgencyDeclaration({
              agency_id: agencyId,
              declaration_type: item.type,
              is_accepted: values[item.key] || false,
            });
          }

          if (isRejectedByAdmin) {
            navigate(clientRoutes.agentCreateAgencyAgentInfo);
          } else {
            const principalAgentId =
              agencyData?.principal_agent_id?._id ||
              agencyData?.principal_agent_id;
            if (principalAgentId) {
              await AgentApi.updateAgentProfile(
                {
                  registration_status:
                    CommonEnums.agentRegistrationStatus.completed,
                },
                principalAgentId,
              );
            }
            navigate(clientRoutes.adminApprovalWaiting);
          }
        } else {
          toast.error(res?.data?.message || "Failed to save brand info.");
        }
      } catch (error) {
        console.error("API Error:", error);
        toast.error("Something went wrong while saving.");
      }

      setState((prev) => ({ ...prev, isLoading: false }));
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  useEffect(() => {
    if (formik.values.profileLogo && formErrors.profileLogo) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated.profileLogo;
        return updated;
      });
    }
  }, [formik.values.profileLogo, formErrors.profileLogo]);

  useEffect(() => {
    setFormErrors((prev) => {
      const updated = { ...prev };

      if (!formik.values.useOfficeLogo && updated.profileLogo) {
        delete updated.profileLogo;
      }

      return updated;
    });
  }, [formik.values.useOfficeLogo]);

  useEffect(() => {
    setFormErrors((prev) => {
      const updated = { ...prev };
      let changed = false;

      if (formik.values.brandLicensing && updated.brandLicensing) {
        delete updated.brandLicensing;
        changed = true;
      }

      if (formik.values.logoUsageRights && updated.logoUsageRights) {
        delete updated.logoUsageRights;
        changed = true;
      }

      return changed ? updated : prev;
    });
  }, [formik.values.brandLicensing, formik.values.logoUsageRights]);

  useEffect(() => {
    const values = formik.values;
    const warnings = {};

    const rgbToHex = (r, g, b) =>
      "#" +
      [r, g, b]
        .map((x) => {
          const hex = Number(x).toString(16);
          return hex.length === 1 ? "0" + hex : hex;
        })
        .join("");

    const getLuminance = (r, g, b) => {
      const a = [r, g, b].map((v) => {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
    };

    const getContrastRatio = (color1, color2) => {
      const hexToRgbObject = (hex) => {
        const cleaned = hex.replace("#", "");
        return {
          r: parseInt(cleaned.substring(0, 2), 16),
          g: parseInt(cleaned.substring(2, 4), 16),
          b: parseInt(cleaned.substring(4, 6), 16),
        };
      };

      const rgb1 = hexToRgbObject(color1);
      const rgb2 = hexToRgbObject(color2);

      const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
      const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);

      return (brightest + 0.05) / (darkest + 0.05);
    };

    const safeHex = (color) => {
      if (!color) return null;
      if (color.hex) return color.hex;
      if (color.rgb?.r != null) {
        return rgbToHex(color.rgb.r, color.rgb.g, color.rgb.b);
      }
      if (color.r != null) {
        return rgbToHex(color.r, color.g, color.b);
      }
      return null;
    };

    const agencyBasePrimary = safeHex(values.agency_base_color_primary);
    const agencyTextPrimary = safeHex(values.agency_text_color_primary);

    if (agencyBasePrimary && agencyTextPrimary) {
      const contrast = getContrastRatio(agencyBasePrimary, agencyTextPrimary);
      if (contrast < 4.5) {
        warnings.agency_primaryContrast =
          "Primary text color is difficult to read on Agency Base Primary color.";
      }
    }

    const agencyBaseSecondary = safeHex(values.agency_base_color_secondary);
    const agencyTextSecondary = safeHex(values.agency_text_color_secondary);

    if (agencyBaseSecondary && agencyTextSecondary) {
      const contrast = getContrastRatio(
        agencyBaseSecondary,
        agencyTextSecondary,
      );
      if (contrast < 4.5) {
        warnings.agency_secondaryContrast =
          "Secondary text color is difficult to read on Agency Base Secondary color.";
      }
    }

    if (state.isBrandAgency && !values.useOfficeLogo) {
      const brandBasePrimary = safeHex(values.brand_base_color_primary);
      const brandTextPrimary = safeHex(values.brand_text_color_primary);

      if (brandBasePrimary && brandTextPrimary) {
        const contrast = getContrastRatio(brandBasePrimary, brandTextPrimary);
        if (contrast < 4.5) {
          warnings.brand_primaryContrast =
            "Primary text color is difficult to read on Brand Base Primary color.";
        }
      }

      const brandBaseSecondary = safeHex(values.brand_base_color_secondary);
      const brandTextSecondary = safeHex(values.brand_text_color_secondary);

      if (brandBaseSecondary && brandTextSecondary) {
        const contrast = getContrastRatio(
          brandBaseSecondary,
          brandTextSecondary,
        );
        if (contrast < 4.5) {
          warnings.brand_secondaryContrast =
            "Secondary text color is difficult to read on Brand Base Secondary color.";
        }
      }
    }

    setContrastWarnings(warnings);
  }, [
    formik.values.agency_base_color_primary,
    formik.values.agency_base_color_secondary,
    formik.values.agency_text_color_primary,
    formik.values.agency_text_color_secondary,
    formik.values.brand_base_color_primary,
    formik.values.brand_base_color_secondary,
    formik.values.brand_text_color_primary,
    formik.values.brand_text_color_secondary,
    formik.values.useOfficeLogo,
    state.isBrandAgency,
  ]);

  const getLogoDisplayUrl = (uploadRecord) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }

    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }

    return "";
  };

  const fetchAgencyData = async () => {
    const localData = LocalStorageHelpers.getAgencyData();

    if (!localData) {
      navigate(clientRoutes.login);
      return;
    }

    const agencyId = localData?._id ?? localData?.id;

    if (!agencyId) {
      navigate(clientRoutes.login);
      return;
    }

    try {
      const res = await AgencyApi.getAgencyById({
        agency_id: agencyId,
      });

      if (!res?.data?.success) {
        throw new Error("Failed to fetch agency");
      }

      const data = res.data.data;
      const isBrand = data?.agency_type === "brand";
      const brandName =
        data?.brand_id?.brand_name || data?.brand_name || "Brand Name";

      if (data?.base_color_primary) {
        formik.setFieldValue(
          "agency_base_color_primary",
          normalizeColor(data.base_color_primary),
        );
      }

      if (data?.base_color_secondary) {
        formik.setFieldValue(
          "agency_base_color_secondary",
          normalizeColor(data.base_color_secondary),
        );
      }

      if (data?.text_color_primary) {
        formik.setFieldValue(
          "agency_text_color_primary",
          normalizeColor(data.text_color_primary),
        );
      }

      if (data?.text_color_secondary) {
        formik.setFieldValue(
          "agency_text_color_secondary",
          normalizeColor(data.text_color_secondary),
        );
      }

      setAgencyOriginalColors((prev) => ({
        base_color_primary: data.base_color_primary
          ? normalizeColor(data.base_color_primary)
          : prev.base_color_primary,
        base_color_secondary: data.base_color_secondary
          ? normalizeColor(data.base_color_secondary)
          : prev.base_color_secondary,
        text_color_primary: data.text_color_primary
          ? normalizeColor(data.text_color_primary)
          : prev.text_color_primary,
        text_color_secondary: data.text_color_secondary
          ? normalizeColor(data.text_color_secondary)
          : prev.text_color_secondary,
      }));

      setState((prev) => ({
        ...prev,
        agencyData: data,
        loadingData: false,
        isBrandAgency: isBrand,
        brandName,
      }));

      await fetchDeclarations(agencyId, isBrand);

      if (data?.profile_image_ref?.original_file_path) {
        formik.setFieldValue(
          "profileLogo",
          data.profile_image_ref.original_file_path,
        );
      } else if (data?.profile_image) {
        formik.setFieldValue("profileLogo", data.profile_image);
      }

      if (data?.agency_base_color) {
        formik.setFieldValue("baseColor", data.agency_base_color);
      }

      if (data?.profile_image_ref) {
        formik.setFieldValue("profileLogoUploadRecord", data.profile_image_ref);
        formik.setFieldValue(
          "profileLogo",
          data.profile_image_ref._id ||
            data.profile_image_ref.original_file_path,
        );
      } else if (data?.profile_image) {
        formik.setFieldValue("profileLogo", data.profile_image);
      }

      if (isBrand) {
        formik.setFieldValue(
          "useOfficeLogo",
          data?.logo_preference === "office_logo",
        );
      }

      if (isBrand && data?.brand_id?._id) {
        setState((prev) => ({ ...prev, isBrandDataLoading: true }));

        try {
          const brandRes = await BrandApi.getBrandById(data.brand_id._id);

          if (brandRes?.data?.success) {
            const brandData = brandRes.data.data;

            // if (brandData?.brand_logo?.original_file_path) {
            //   formik.setFieldValue(
            //     "brand_logo",
            //     brandData.brand_logo.original_file_path,
            //   );
            // }

            if (brandData?.base_color_primary) {
              formik.setFieldValue(
                "brand_base_color_primary",
                normalizeColor(brandData.base_color_primary),
              );
            }

            if (brandData?.base_color_secondary) {
              formik.setFieldValue(
                "brand_base_color_secondary",
                normalizeColor(brandData.base_color_secondary),
              );
            }

            if (brandData?.text_color_primary) {
              formik.setFieldValue(
                "brand_text_color_primary",
                normalizeColor(brandData.text_color_primary),
              );
            }

            if (brandData?.text_color_secondary) {
              formik.setFieldValue(
                "brand_text_color_secondary",
                normalizeColor(brandData.text_color_secondary),
              );
            }

            if (brandData?.theme_colors_response?.length > 0) {
              formik.setFieldValue(
                "brand_theme_colors",
                brandData.theme_colors_response,
              );
            }

            if (brandData?.brand_logo) {
              formik.setFieldValue(
                "brandLogoUploadRecord",
                brandData.brand_logo,
              );
              formik.setFieldValue(
                "brand_logo",
                brandData.brand_logo._id ||
                  brandData.brand_logo.original_file_path,
              );
            }

            setBrandOriginalColors({
              base_color_primary: normalizeColor(brandData.base_color_primary),
              base_color_secondary: normalizeColor(
                brandData.base_color_secondary,
              ),
              text_color_primary: normalizeColor(brandData.text_color_primary),
              text_color_secondary: normalizeColor(
                brandData.text_color_secondary,
              ),
            });

            setState((prev) => ({
              ...prev,
              brandStatus: brandData?.status,
              isBrandDataLoading: false,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch brand data", err);
          setState((prev) => ({ ...prev, isBrandDataLoading: false }));
        }
      }
    } catch (error) {
      console.error("Error fetching agency:", error);
      setState((prev) => ({
        ...prev,
        loadingData: false,
        agencyData: localData,
      }));
    }
  };

  useEffect(() => {
    fetchAgencyData();
  }, []);

  useEffect(() => {
    if (showMobilePreview) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [showMobilePreview]);

  const handleLogout = () => {
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const handleLogoutConfirm = () => {
    closeLogoutModal();
    LocalStorageHelpers.clear();
    navigate(clientRoutes.login);
  };

  const handleBack = () => {
    navigate(clientRoutes.agentCreateAgencyLegalInfo);
  };

  const handleEditBrand = () => {
    navigate(clientRoutes.agentCreateAgencyLegalInfo);
  };

  const handleDeclarationChange = (fieldName, checked) => {
    formik.setFieldValue(fieldName, checked);
  };

  // const isBrandLogoUploaded = !!formik.values.brand_logo;
  const isBrandLogoUploaded =
    !!formik.values.brandLogoUploadRecord || !!formik.values.brand_logo;
  const isOfficeLogoUploaded = !!formik.values.profileLogo;

  const isPreviewReady = state.isBrandAgency
    ? formik.values.useOfficeLogo
      ? isOfficeLogoUploaded
      : isBrandLogoUploaded
    : isOfficeLogoUploaded;

  const isBrandColorLocked =
    state.isBrandAgency &&
    state.brandStatus === "active" &&
    !!formik.values.brand_logo;

  if (state.loadingData) {
    return (
      <ProtectedComponent>
        <div className="brand-grammar-container">
          <div
            className="circular-progress-container"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
            }}
          >
            <CustomPageLoader />
          </div>
        </div>
      </ProtectedComponent>
    );
  }

  return (
    <ProtectedComponent>
      <AgentCreatingAgencySteps
        step={CommonEnums.agencyProfileSteps.brandGrammer}
        agencyData={state.agencyData}
      />

      <div className="brand-grammar-container">
        <form onSubmit={formik.handleSubmit}>
          <div className="brand-grammar-content">
            <div className="left-column">
              {state.isBrandAgency ? (
                <>
                  <div className="connected-brand-box">
                    <div className="brand-header">
                      <span>Connected Brand/Franchise</span>
                      <span className="edit-link" onClick={handleEditBrand}>
                        Edit
                      </span>
                    </div>
                    <div className="brand-name">{state.brandName}</div>
                  </div>

                  <div className="grammar-section">
                    <div className="section-title">
                      Upload Brand/Franchise Logo*
                    </div>
                    <div className="brand-section-desc brand-desc">
                      Your agency is connected with a Brand/Franchise.
                      <br /> To complete your profile, upload the parent Brand's
                      official logo using the specifications below.
                      <ul>
                        <li>
                          <span className="office-logo">Dimensions:</span>{" "}
                          Recommended size is 510 x 96 pixels.
                        </li>
                        <li>
                          <span className="office-logo">Background:</span> Must
                          be a solid background (no transparency) for optimal
                          color generation.
                        </li>
                        <li>
                          <span className="office-logo">Quality:</span> Please
                          provide a high-resolution file to ensure your branding
                          remains crisp on social media posts.
                        </li>
                        <li>Accepted files are PNG, JPG, or JPEG.</li>
                      </ul>
                    </div>

                    {state.isBrandDataLoading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          padding: "20px",
                        }}
                      >
                        <CircularProgress />
                      </div>
                    ) : (
                      <BrandProfileLogo
                        formik={formik}
                        brandId={state.agencyData?.brand_id?._id}
                        agencyId={state.agencyData?._id}
                        brandStatus={state.brandStatus}
                        onColorsGenerated={(colors) => {
                          setBrandOriginalColors({
                            base_color_primary: normalizeColor(
                              colors.base_color_primary,
                            ),
                            base_color_secondary: normalizeColor(
                              colors.base_color_secondary,
                            ),
                            text_color_primary: normalizeColor(
                              colors.text_color_primary,
                            ),
                            text_color_secondary: normalizeColor(
                              colors.text_color_secondary,
                            ),
                          });
                        }}
                        onUploadComplete={async () => {
                          await fetchAgencyData();
                        }}
                      />
                    )}
                  </div>

                  <div className="grammar-section">
                    <div className="section-title">
                      Brand/Franchisee Colors*
                    </div>
                    <div className="section-desc brand-desc">
                      These colors have been generated from your Brand/Franchise
                      logo to ensure consistent styling across your profile.
                    </div>

                    {state.isBrandDataLoading ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          padding: "20px",
                        }}
                      >
                        <CircularProgress />
                      </div>
                    ) : isBrandLogoUploaded ? (
                      <ThemePreferences
                        formik={formik}
                        isLocked={isBrandColorLocked}
                        prefix="brand"
                        originalColors={brandOriginalColors}
                      />
                    ) : (
                      <div className="placeholder-box">
                        <p>
                          Upload Brand/Franchisee logo to generate the colors
                        </p>
                      </div>
                    )}
                  </div>

                  <ThemeErrorSummary
                    errors={contrastWarnings}
                    prefix="brand"
                    title="Errors in Agency Brand Colors!"
                  />

                  <div className="office-logo-switch-container">
                    <div className="text-content">
                      <div className="title-row">
                        <h6>Use office logo</h6>

                        <Switch
                          checked={formik.values.useOfficeLogo}
                          onChange={(e) =>
                            formik.setFieldValue(
                              "useOfficeLogo",
                              e.target.checked,
                            )
                          }
                          disabled={false}
                          color="error"
                        />
                      </div>

                      <p>
                        Replace the franchise logo with your office logo across
                        all property marketing
                      </p>

                      <div className="info-notice-box">
                        <img
                          src="/images/blue-info-icon.svg"
                          alt="info"
                          className="info-icon"
                        />

                        <div className="info-text-content">
                          <p className="info-title">
                            Applies to all property marketing
                          </p>
                          <p className="info-desc">
                            This setting will be used for all designs, captions
                            and social posts.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {formik.values.useOfficeLogo && (
                    <>
                      <div className="grammar-section">
                        <div className="section-title">
                          Upload Brand Office Logo*
                        </div>
                        <div className="brand-section-desc">
                          Upload your office-specific logo that represents your
                          agency's connection with {state.brandName}. Ensure
                          your logo matches the following requirements:
                          <ul>
                            <li>
                              <span className="office-logo">Dimensions:</span>{" "}
                              Recommended size is 510 x 96 pixels.
                            </li>
                            <li>
                              <span className="office-logo">Background:</span>{" "}
                              Use a solid background (no transparency).
                            </li>
                            <li>
                              <span className="office-logo">Quality:</span>{" "}
                              Upload original, high-resolution files.
                            </li>
                            <li>Accepted files are PNG, JPG, or JPEG.</li>
                          </ul>
                        </div>

                        <AgencyProfileLogo
                          formik={formik}
                          useOfficeLogo={formik.values.useOfficeLogo}
                          agencyId={state.agencyData?._id}
                          onColorsGenerated={(colors) => {
                            setAgencyOriginalColors({
                              base_color_primary: normalizeColor(
                                colors.base_color_primary,
                              ),
                              base_color_secondary: normalizeColor(
                                colors.base_color_secondary,
                              ),
                              text_color_primary: normalizeColor(
                                colors.text_color_primary,
                              ),
                              text_color_secondary: normalizeColor(
                                colors.text_color_secondary,
                              ),
                            });
                          }}
                          onUploadComplete={async () => {
                            await fetchAgencyData(); // ✅ ADD THIS
                          }}
                        />
                      </div>

                      <div className="grammar-section">
                        <div className="section-title">
                          Brand Office Colors*
                        </div>
                        <div className="section-desc">
                          These colors are automatically extracted from your
                          uploaded Office Logo to ensure consistent styling
                          across your profile.
                        </div>

                        {isOfficeLogoUploaded ? (
                          <EditThemePreferences
                            formik={formik}
                            prefix="agency"
                            originalColors={agencyOriginalColors}
                          />
                        ) : (
                          <div className="placeholder-box">
                            <p>
                              Upload agency logo to generate the agency colors
                            </p>
                          </div>
                        )}
                      </div>

                      <ThemeErrorSummary
                        errors={contrastWarnings}
                        prefix="agency"
                        title="Errors in Agency Colors!"
                      />
                    </>
                  )}

                  <div className="grammar-section declaration-section">
                    <div className="section-title">
                      Brand & Licensing Declaration
                    </div>
                    <div className="section-desc brand-desc">
                      {/* Please confirm your authorization to use the provided
                      branding assets for{" "}
                      <span className="brand-name">{state.brandName}</span>. */}
                      Please review and confirm the following declarations
                      before continuing
                    </div>

                    <div
                      className="declaration-item"
                      onClick={() =>
                        handleDeclarationChange(
                          "brandLicensing",
                          !formik.values.brandLicensing,
                        )
                      }
                    >
                      <input
                        type="checkbox"
                        checked={formik.values.brandLicensing}
                        onChange={(e) =>
                          handleDeclarationChange(
                            "brandLicensing",
                            e.target.checked,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="decl-content">
                        <p className="brand-licence">Brand Licensing*</p>
                        <span>
                          I confirm that my agency is formally connected with or
                          a franchisee of {state.brandName}.
                        </span>
                      </div>
                    </div>

                    <div
                      className="declaration-item"
                      onClick={() =>
                        handleDeclarationChange(
                          "logoUsageRights",
                          !formik.values.logoUsageRights,
                        )
                      }
                    >
                      <input
                        type="checkbox"
                        checked={formik.values.logoUsageRights}
                        onChange={(e) =>
                          handleDeclarationChange(
                            "logoUsageRights",
                            e.target.checked,
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="decl-content">
                        <p className="brand-licence">Logo Usage Rights*</p>
                        <span>
                          I certify that I am authorized to use and display the
                          provided Brand/Franchise & Brand Office logo for
                          marketing and profile purposes.
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grammar-section">
                    <div className="section-title">Upload Agency Logo</div>
                    <div className="section-desc">
                      Provide your agency’s official logo to personalize your
                      marketing collateral and maintain brand consistency.
                    </div>
                    <div className="warning-contain">
                      <ul className="logo-info-list">
                        <li>
                          For best results, we recommend that you upload your
                          logo 510 pixels wide by 96 pixels high
                        </li>
                        <li>
                          Ensure your logo does not have a transparent
                          background
                        </li>
                        <li>
                          Please upload original image file, images with low
                          quality results in improper results
                        </li>
                        <li>Allowed png, jpg and jpeg formats only</li>
                      </ul>
                    </div>

                    <AgencyProfileLogo
                      formik={formik}
                      agencyId={state.agencyData?._id}
                      onColorsGenerated={(colors) => {
                        setAgencyOriginalColors({
                          base_color_primary: normalizeColor(
                            colors.base_color_primary,
                          ),
                          base_color_secondary: normalizeColor(
                            colors.base_color_secondary,
                          ),
                          text_color_primary: normalizeColor(
                            colors.text_color_primary,
                          ),
                          text_color_secondary: normalizeColor(
                            colors.text_color_secondary,
                          ),
                        });
                      }}
                      onUploadComplete={async () => {
                        await fetchAgencyData();
                      }}
                    />
                  </div>

                  <div className="grammar-section">
                    <div className="section-title">Agency Brand Colors</div>
                    <div className="section-desc grammer-desc">
                      Maintain a consistent visual identity across all marketing
                      content by providing your official brand palette.
                    </div>

                    {isOfficeLogoUploaded ? (
                      <EditThemePreferences
                        formik={formik}
                        prefix="agency"
                        originalColors={agencyOriginalColors}
                      />
                    ) : (
                      <div className="placeholder-box">
                        <p>Upload agency logo to generate the agency colors</p>
                      </div>
                    )}
                  </div>

                  <ThemeErrorSummary
                    errors={contrastWarnings}
                    prefix="agency"
                    title="Errors in Agency Colors!"
                  />
                </>
              )}

              <ErrorSummary errors={formErrors} />

              <div className="action-buttons">
                <button type="button" className="back-btn" onClick={handleBack}>
                  Back
                </button>
                <button
                  className="continue-btn"
                  disabled={state?.isLoading}
                  type="submit"
                >
                  {state?.isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Save & continue"
                  )}
                </button>
              </div>

              <div className="form-footer">
                <p>
                  By continuing, you agree to our <a>Terms and Conditions</a>
                </p>
                <p>
                  Read our <a>Personal Information Collection Statement</a>
                </p>
                <p style={{ marginTop: "20px", color: "#666" }}>
                  Profile progress saved.
                  <br />
                  Feel free to log out; you can continue your setup at any time.
                </p>
                <span className="logout-link" onClick={openLogoutModal}>
                  Logout
                </span>
              </div>
            </div>

            <div className="right-column">
              <div className="grammar-section">
                <div className="section-title">
                  Preview Post with Brand Info
                </div>
                <div className="section-desc right-grammer-desc">
                  A live look at your customized marketing content, styled with
                  your agency’s unique brand identity.
                </div>

                <div className="preview-wrapper">
                  <div className="preview-card">
                    {isPreviewReady ? (
                      <img
                        src="/images/brand_grammer_sample.png"
                        alt="Post Preview"
                        className="live-preview-img"
                      />
                    ) : (
                      <div className="empty-preview-content">
                        <img
                          src="/icons/pending-icon-2.png"
                          alt="Waiting Icon"
                          className="waiting-icon"
                        />
                        <p>Upload agency logo to generate the preview</p>
                      </div>
                    )}
                  </div>

                  {isPreviewReady && (
                    <div className="preview-actions">
                      <button
                        type="button"
                        onClick={() => setShowMobilePreview(true)}
                      >
                        Preview
                      </button>
                      <button type="button">Download</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <div className={`mobile-bottom-drawer ${isDrawerOpen ? "open" : ""}`}>
        <div className="drawer-header" onClick={() => setIsDrawerOpen(true)}>
          <div className="drawer-handle-line"></div>
          <span className="drawer-title">Post preview </span>
        </div>

        <div className="drawer-content">
          <div className="drawer-scroll-area">
            <div className="mobile-preview-card">
              <div className="preview-text-info">
                <h3>Preview Post with Brand Info</h3>
                <p>
                  A live look at your customized marketing content, styled with
                  your agency’s unique brand identity
                </p>
              </div>

              {isPreviewReady ? (
                <img
                  src="/images/brand_grammer_sample.png"
                  alt="Post Preview"
                  className="live-preview-img"
                />
              ) : (
                <div className="empty-preview-content">
                  <img
                    src="/icons/pending-icon-2.png"
                    alt="Waiting Icon"
                    className="waiting-icon"
                  />
                  <p>Upload agency logo to generate the preview</p>
                </div>
              )}

              {isPreviewReady && (
                <div className="drawer-actions">
                  <button type="button" className="btn-preview">
                    Preview
                  </button>
                  <button type="button" className="btn-download">
                    Download
                  </button>
                </div>
              )}

              <button
                className="close-drawer-btn"
                onClick={() => setIsDrawerOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isLogoutModalOpen}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") closeLogoutModal();
          else closeLogoutModal();
        }}
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-desc"
        className="logout-confirm-modal"
      >
        <div className="logout-modal-center">
          <div className="logout-modal-card">
            <h3 id="logout-modal-title" className="logout-modal-title">
              Log out?
            </h3>

            <p id="logout-modal-desc" className="logout-modal-subtitle">
              Unsaved changes will be discarded.
              <br />
              Are you sure you want to proceed?
            </p>

            <div className="logout-modal-actions">
              <button
                type="button"
                className="logout-cancel-btn"
                onClick={closeLogoutModal}
              >
                Cancel
              </button>

              <button
                type="button"
                className="logout-confirm-btn"
                onClick={handleLogoutConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </ProtectedComponent>
  );
};

export default AgentAgencyBrandGrammar;
